  import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { formatTimeTo12Hour, getCurrentDateAndTime, GlobalService } from "../../../shared";
import { createAvaibility, getUserAvailabilities, UpdateUserAvailabilities, deleteUserAvailabilities, useAppDispatch } from "../../../../redux";
import { Edit } from "lucide-react";
import AvailabilityModal from "../../modals/AvailabilityModal";
import Button from "../../../../components/ui/Button";
import Card from "../../../../components/ui/card";
import { useTranslation } from "react-i18next";
const defaultDays = {
    monday: { start: "", end: "", active: false },
    tuesday: { start: "", end: "", active: false },
    wednesday: { start: "", end: "", active: false },
    thursday: { start: "", end: "", active: false },
    friday: { start: "", end: "", active: false },
    saturday: { start: "", end: "", active: false },
    sunday: { start: "", end: "", active: false },
};

export default function Availability({ userId, isEmergency = false, showEditIcon = true }: { userId?: string; isEmergency?: boolean; showEditIcon?: boolean }) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { user } = useSelector((state: any) => state.auth);
    const { availabilities, emergencyAvailabilities } = useSelector((state: any) => state.professional);
    const targetUserId = userId || user?.id; // Use passed userId or fallback to current user
    const [showModal, setShowModal] = useState(false);
    const [regularHours, setRegularHours] = useState(defaultDays);
    const [originalRegularHours, setOriginalRegularHours] = useState(defaultDays);
    const [resetRegularData, setResetRegularData] = useState(false);

    useEffect(() => {
        if (availabilities && availabilities.length > 0) {
            const updated = { ...defaultDays };

            availabilities.forEach(item => {
                const day = item.day?.toLowerCase();
                if (updated[day]) {
                    updated[day] = {
                        id: item.id, // keep id if needed for updates
                        start: getCurrentDateAndTime(new Date(item.startAt)).substring(11, 16),
                        end: getCurrentDateAndTime(new Date(item.endAt)).substring(11, 16),
                        active: item.isAvailable,
                    };
                }
            });
            setOriginalRegularHours(updated);
            setRegularHours(updated); // or split based on `type` if you have both types
        } else {
            // Reset to default when no regular availabilities exist
            setOriginalRegularHours(defaultDays);
            setRegularHours(defaultDays);
        }
    }, [availabilities]);

    useEffect(() => {
        getUserAvailabilitiesFromServer();
    }, [targetUserId]);

    const getUserAvailabilitiesFromServer = async () => {
        if (!targetUserId) return;
        await dispatch(getUserAvailabilities({ id: targetUserId }));
    };

    const handleOpenModal = () => {
        // Check if emergency hours already exist
        if (emergencyAvailabilities && emergencyAvailabilities.length > 0) {
            GlobalService.showToast(
                "warning",
                t("availability.restrictedAccess"),
                t("availability.restrictedMessage")
            );
            return;
        }
        setShowModal(true);
    };
    const handleSave = async (updatedHours: any) => {
        if (resetRegularData) {
            // Reset regular data - delete all regular availabilities
            if (availabilities && availabilities.length > 0) {
                for (const availability of availabilities) {
                    try {
                        await dispatch(deleteUserAvailabilities({ id: availability.id }));
                    } catch (error) {
                        console.error("Error deleting regular availability:", error);
                    }
                }
            }

            console.log("Regular data reset successfully");
            GlobalService.showToast("success", t("availability.successTitle"), t("availability.resetSuccessMessage"));
            
            // Reset local state
            setRegularHours(defaultDays);
            setOriginalRegularHours(defaultDays);
            
            setShowModal(false);
            return;
        }

        const newItems = [];
        let updatedCount = 0;
        let hasValidationError = false;

        // First validate all active days have both start and end times
        for (const day in updatedHours) {
            const updated = updatedHours[day];

            if (updated.active && (!updated.start || !updated.end)) {
                GlobalService.showToast(
                    "error",
                    t("availability.validationError"),
                    t("availability.setBothTimesMessage", { day: day.charAt(0).toUpperCase() + day.slice(1) })
                );
                hasValidationError = true;
            }
        }

        if (hasValidationError) {
            return; // Don't proceed with saving if there are validation errors
        }

        for (const day in updatedHours) {
            const original = originalRegularHours[day];
            const updated = updatedHours[day];

            // Only continue if something has changed
            const hasChanged =
                original.start !== updated.start || original.end !== updated.end || original.active !== updated.active;

            if (!hasChanged) continue;

            // Skip if day is not active (we don't need to process inactive days)
            if (!updated.active) continue;

            const startAt = new Date();
            const endAt = new Date();

            const [startHour, startMin] = updated.start.split(":");
            const [endHour, endMin] = updated.end.split(":");
            startAt.setHours(+startHour, +startMin);
            endAt.setHours(+endHour, +endMin);

            const payloadItem = {
                isEmergency: false,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                userId: { id: user?.id },
                startAt: startAt.toISOString(),
                endAt: endAt.toISOString(),
                isAvailable: updated.active,
                day: day.charAt(0).toUpperCase() + day.slice(1),
            };

            if (updated.id) {
                // Update existing availability
                await dispatch(UpdateUserAvailabilities({ id: updated.id, data: payloadItem }));
                updatedCount++;
            } else {
                newItems.push(payloadItem);
            }
        }

        // Create new items in batch
        if (newItems.length > 0) {
            try {
                await dispatch(createAvaibility({ data: newItems }));
                GlobalService.showToast(
                    "success",
                    t("availability.successTitle"),
                    newItems.length > 1
                        ? t("availability.newAvailabilitiesCreated")
                        : t("availability.newAvailabilityCreated")
                );
            } catch (error) {
                GlobalService.showToast("error", "Error", t("availability.createError"));
            }
        }

        if (updatedCount > 0) {
            GlobalService.showToast(
                "success",
                t("availability.successTitle"),
                updatedCount > 1 ? t("availability.availabilitiesUpdated") : t("availability.availabilityUpdated")
            );
        }

        if (newItems.length > 0 || updatedCount > 0) {
            setShowModal(false); // Close modal only if changes were made
        }
    };
    return (
        <>
            <Card color={undefined} className="p-6">
                <div className="flex justify-between items-start mb-4">
                    <h2 className="text-xl font-semibold">{t("availability.title")}</h2>
                    {showEditIcon && (
                        <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700" onClick={handleOpenModal}>
                            <Edit className="w-4 h-4" />
                        </Button>
                    )}
                </div>
                <div className="space-y-4">
                    <div>
                        <h3 className="font-medium mb-2">{t("availability.regularHours")}</h3>
                        <div className="grid grid-cols-1 gap-4">
                            {Object.entries(regularHours).map(
                                ([day, hours]) =>
                                    hours.active && (
                                        <div key={day} className="flex items-center justify-between">
                                            <span className="capitalize">{day}</span>
                                            {formatTimeTo12Hour(hours.start)} - {formatTimeTo12Hour(hours.end)}
                                        </div>
                                    )
                            )}
                        </div>
                    </div>
                </div>
            </Card>
            <AvailabilityModal
                isEmergency={isEmergency}
                open={showModal}
                regularHours={regularHours}
                handleSave={handleSave}
                onClose={() => setShowModal(false)}
                resetData={resetRegularData}
                onResetDataChange={setResetRegularData}
            />
        </>
    );
}